'use client'

import { useState } from 'react'
import { BlogCard } from '@/components/ui/blog-card'
import { Button } from '@/components/ui/button'
import { ContentModuleImpression, LIST_IMPRESSION_THRESHOLD } from '@/components/analytics/content-module-impression'
import { Card, CardContent } from '@/components/ui/card'
import { BookOpen, ChevronDown } from 'lucide-react'
import Link from 'next/link'

interface AuthorArticlesGridProps {
  posts: any[]
  authorName: string
  initialCount?: number
  loadMoreCount?: number
}

export function AuthorArticlesGrid({
  posts,
  authorName,
  initialCount = 6,
  loadMoreCount = 6
}: AuthorArticlesGridProps) {
  const [visibleCount, setVisibleCount] = useState(initialCount)

  const visiblePosts = posts.slice(0, visibleCount)
  const hasMorePosts = visibleCount < posts.length
  const remainingCount = posts.length - visibleCount

  const handleLoadMore = () => {
    setVisibleCount(prev => Math.min(prev + loadMoreCount, posts.length))
  }

  if (posts.length === 0) {
    return (
      <Card className="bg-white shadow-md border-0">
        <CardContent className="p-8 text-center">
          <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No articles yet
          </h3>
          <p className="text-gray-600 mb-4">
            {authorName} hasn&apos;t published any articles yet. Check back soon!
          </p>
          <Button asChild variant="outline" className="border-green-200 text-green-700 hover:bg-green-50">
            <Link href="/blog">
              Browse all articles
            </Link>
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div>
      {/* Articles Grid */}
      <ContentModuleImpression
        module="author_card"
        count={visiblePosts.length}
        threshold={LIST_IMPRESSION_THRESHOLD}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {visiblePosts.map((post: any) => (
          <BlogCard
            key={post.id}
            variant="compact"
            post={{
              id: post.id,
              title: post.title,
              excerpt: post.excerpt || '',
              category: post.category,
              date_published: post.date_published || post.date_created || '',
              image: post.image_url || post.image || '',
              image_alt: post.image_alt,
              slug: post.slug,
              reading_time: post.reading_time
            }}
          />
        ))}
      </ContentModuleImpression>

      {/* Load More Button */}
      {hasMorePosts && (
        <div className="flex justify-center mt-8">
          <Button
            onClick={handleLoadMore}
            variant="outline"
            size="lg"
            className="border-green-200 text-green-700 hover:bg-green-50 hover:border-green-300"
          >
            <ChevronDown className="w-5 h-5 mr-2" />
            Load More ({remainingCount} remaining)
          </Button>
        </div>
      )}

      {/* Show count indicator */}
      {posts.length > initialCount && (
        <p className="text-center text-gray-500 text-sm mt-4">
          Showing {Math.min(visibleCount, posts.length)} of {posts.length} articles
        </p>
      )}
    </div>
  )
}
